import * as yup from "yup";
import { useNavigate } from "react-router-dom";

import {
  Avatar,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  Dialog,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Slide,
  Switch,
  Box,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import { countries } from "countries-list";
import { roleInProject } from "config";
import { useTechSkills } from "../../../hooks/useSkills";
import ProjectDisplay from "components/ProjectDisplay";
import { usePatchCvMutation } from "../../../lib/cvsSlice";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";

import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import Select from "react-select";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const countryOptions = [
  { value: "", label: "Select Country" },
  ...Object.values(countries)
    .map((c) => ({ value: c.name, label: c.name }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const dateToMonthYear = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return { month: "", year: "" };
  return {
    month: MONTH_NAMES[date.getMonth()],
    year: String(date.getFullYear()),
  };
};

const monthYearToDate = (month, year) => {
  if (!month || !year) return null;
  const idx = MONTH_NAMES.indexOf(month);
  if (idx === -1) return null;
  return new Date(parseInt(year, 10), idx, 1);
};

const setMonthToNumber = (month) => {
  switch (month) {
    case "January":
      return 1;
    case "February":
      return 2;
    case "March":
      return 3;
    case "April":
      return 4;
    case "May":
      return 5;
    case "June":
      return 6;
    case "July":
      return 7;
    case "August":
      return 8;
    case "September":
      return 9;
    case "October":
      return 10;
    case "November":
      return 11;
    case "December":
      return 12;

    default:
      return "neutral";
  }
};

const projRegex = /^https:\/\/[a-zA-Z0-9.-]+(\.[a-zA-Z]{2,}){1,}([\/\S]*)?$/;

const projectSchema = yup.object().shape({
  name: yup.string().required("Project name is required"),
  onGoing: yup.boolean(),
  startMonth: yup.string().required("Month started is required"),
  startYear: yup.string().required("Year started is required"),
  endYear: yup.string().when("onGoing", {
    is: false,
    then: yup
      .string()
      .test("endYear", "End year is required year", function (value, context) {
        var endYear = new Date(value);
        var specificEndYear = endYear.getFullYear();
        var specificStartYear = new Date(
          context.parent.startYear
        ).getFullYear();

        return specificEndYear >= specificStartYear;
      }),
    notRequired: yup.boolean(),
  }),

  endMonth: yup.string().when(["onGoing", "startYear", "endYear"], {
    is: (onGoing) => !onGoing,
    then: yup
      .string()
      .test(
        "endMonth",
        "End month for the same year cannot be before start month",
        function (value, context) {
          const startMonth = context.parent.startMonth;
          const endMonth = value;

          return setMonthToNumber(endMonth) >= setMonthToNumber(startMonth);
        }
      ),
    otherwise: yup.string().nullable(),
  }),
  sector: yup.string().required("Sector is required"),
  skill: yup.array().required("Skill is required"),

  customerName: yup.string().required("Customer name is required"),
  customerLocation: yup.string(),
  roleInProject: yup.array(),
  description: yup.string(),
  /*  projectUrl: yup.string().matches(projRegex, "Enter a valid link to project"), */
  /* keyAssignments: yup.boolean(), */
  excludeInCv: yup.boolean(),
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const Projects = ({ projs = [], cvId, label }) => {
  const { options: techSkillOptions } = useTechSkills();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const navigate = useNavigate();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();
  const [projectId, setProjectId] = useState();
  const [projectElement, setProjectElement] = useState(null);
  const [logStartYear, setLogStartYear] = useState();
  const [logEndYear, setLogEndYear] = useState();
  const [selectedRole, setSelectedRole] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState([]);
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});
  const [projectsState, setProjectsState] = useState(() => {
    const savedProjects = localStorage.getItem(`projectsState_${cvId}`);
    return savedProjects ? JSON.parse(savedProjects) : projs;
  });

  useEffect(() => {
    localStorage.setItem(`projectsState_${cvId}`, JSON.stringify(projectsState));
  }, [projectsState]);

  useEffect(() => {
    const savedProjects = JSON.parse(localStorage.getItem(`projectsState_${cvId}`)) || [];
    // Get current order of IDs in projectsState
    const currentOrder = projectsState.map((p) => p._id);
    // Filter saved projects to keep only those in projs (remove deleted)
    const currentProjects = savedProjects.filter((p) =>
      projs.some((proj) => proj._id === p._id)
    );
    // Add new projects from projs
    const newProjects = projs.filter(
      (proj) => !currentProjects.some((p) => p._id === proj._id)
    );
    // Reorder to maintain current drag order, appending new projects
    const updatedProjects = [
      ...currentProjects.sort((a, b) => {
        const aIndex = currentOrder.indexOf(a._id);
        const bIndex = currentOrder.indexOf(b._id);
        return aIndex - bIndex;
      }),
      ...newProjects,
    ];
    // Only update if projectsState has changed to avoid infinite loops
    if (JSON.stringify(updatedProjects) !== JSON.stringify(projectsState)) {
      setProjectsState(updatedProjects);
    }
  }, [projs]);


  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };
  const handleClose = () => {
    reset(); // Reset the form fields

    setSelectedRole([]);
    setSelectedSkill([]);
    setLogStartYear();
    setLogEndYear();
    setValue("onGoing", false);
    setOpen(false);
  };

  //disable key assignments checkbox after two projects
  /* const isCheckboxDisabled =
    projs.filter((proj) => proj.keyAssignments === true).length >= 2; */

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { values, errors },
  } = useForm({
    resolver: yupResolver(projectSchema),
    defaultValues: {
      name: "",
      onGoing: false,
      startMonth: "",
      startYear: "",
      endMonth: "",
      endYear: "",
      sector: "",
      skill: [],
      customerName: "",
      customerLocation: "",
      roleInProject: [],
      description: "",
      /* projectUrl: "", */
      /* keyAssignments: false, */
      excludeInCv: false,
    },
  });

  const [patchCv] = usePatchCvMutation();
  const submitResults = async (e) => {
    const newProj = e;

    await patchCv({ cvId, projects: [newProj] })
      .then((result) => {
        handleClose();
        projs.push(newProj);
      })
      .catch((error) => {
        console.log("Add new project error:", error);
      });

  };

  const handleRemove = async (item) => {
    await patchCv({ cvId, projects: [{ id: item, delete: true }] }).unwrap();
    const updatedCvProjects = projectsState.filter(
      (project) => project.id !== item
    );
    setProjectsState(updatedCvProjects);
    projs.splice(
      projs.findIndex((project) => project._id === item),
      1
    );

    toast.info("Project removed successfully");
  };

  const handleEdit = async (projectId, mode) => {
    setOpen(true);
    setMode(mode);
    setProjectId(projectId);
  };

  useFormPersist("projects", {
    watch,
    setValue,
    storage: window.localStorage, // default window.sessionStorage
  });

  useEffect(() => {
    const data = localStorage.getItem("projects");
    if (data !== null) JSON.parse(data);
  }, []);

  useEffect(() => {
    localStorage.setItem("projects", JSON.stringify(projs));
  }, [projs]);

  useEffect(() => {
    const initialLocalExcludeInCv = {};
    projs.forEach((proj) => {
      const savedState = localStorage.getItem(`excludeInCv-${proj._id}`);
      initialLocalExcludeInCv[proj._id] = savedState === "true";
    });

    setLocalExcludeInCv(initialLocalExcludeInCv);
  }, [projs]);

  const handleInputChange = (event, projectId, fieldName) => {
    const updatedProjects = projectsState.map((project) =>
      project._id === projectId
        ? { ...project, [fieldName]: event.target.value }
        : project
    );
    setProjectsState(updatedProjects);
    localStorage.setItem(`projectsState_${cvId}`, JSON.stringify(updatedProjects));
  };

  const handleProjectUpdate = (projectId, updates) => {
    const updatedProjects = projectsState.map((project) =>
      project._id === projectId ? { ...project, ...updates } : project
    );
    setProjectsState(updatedProjects);
    localStorage.setItem(`projectsState_${cvId}`, JSON.stringify(updatedProjects));
  };

  const handleToggleChange = async (
    newExcludeInCv,
    projectId,
    fieldName,
    updatedValue
  ) => {
    try {
      const updatedProjects = projs.map((proj) => {
        if (proj._id === projectId) {
          return {
            ...proj,
            [fieldName]: updatedValue,
          };
        }
        return proj;
      });

      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [projectId]: newExcludeInCv,
      }));
      localStorage.setItem(`excludeInCv-${projectId}`, newExcludeInCv.toString());

      const projToUpdate = updatedProjects.find((p) => p._id === projectId);
      await patchCv({
        cvId,
        projects: [{ _id: projectId, ...projToUpdate, excludeInCv: newExcludeInCv }],
      }).unwrap();

      setProjectsState(updatedProjects);
    } catch (error) {
      console.error("Error updating toggle:", error);
    }
  };

  const projectElmnt = projectsState.find((proj) => proj._id === projectId);
  const renderSingleElement = () => {
    const projSkills = projectElmnt?.skill || [];
    const projRoles = projectElmnt?.roleInProject || [];
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ marginTop: "10px" }}
        >
          <Grid item xs={12}>
            <TextField
              label="Project Name"
              placeholder="Enter Name"
              fullWidth
              autoFocus
              value={projectElmnt?.name || ""}
              onChange={(e) => handleInputChange(e, projectId, "name")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Sector"
              placeholder="Enter Name"
              fullWidth
              value={projectElmnt?.sector || ""}
              onChange={(e) => handleInputChange(e, projectId, "sector")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="Start Date"
              inputFormat="dd-MM-yyyy"
              value={monthYearToDate(projectElmnt?.startMonth, projectElmnt?.startYear)}
              onChange={(date) => {
                const { month, year } = dateToMonthYear(date);
                handleProjectUpdate(projectId, { startMonth: month, startYear: year });
              }}
              renderInput={(params) => (
                <TextField {...params} fullWidth InputLabelProps={{ shrink: true }} />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DatePicker
              label="End Date"
              inputFormat="dd-MM-yyyy"
              disabled={projectElmnt?.onGoing}
              value={projectElmnt?.onGoing ? null : monthYearToDate(projectElmnt?.endMonth, projectElmnt?.endYear)}
              onChange={(date) => {
                const { month, year } = dateToMonthYear(date);
                handleProjectUpdate(projectId, { endMonth: month, endYear: year });
              }}
              renderInput={(params) => (
                <TextField {...params} fullWidth placeholder="DD-MM-YYYY" InputLabelProps={{ shrink: true }} />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              label="Ongoing"
              control={
                <Checkbox
                  checked={projectElmnt?.onGoing || false}
                  onChange={(e) => {
                    handleProjectUpdate(projectId, { onGoing: e.target.checked });
                    if (e.target.checked) {
                      handleProjectUpdate(projectId, { endMonth: "", endYear: "" });
                    }
                  }}
                />
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Customer Name"
              placeholder="Enter Name"
              fullWidth
              value={projectElmnt?.customerName || ""}
              onChange={(e) => handleInputChange(e, projectId, "customerName")}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Customer Location"
              fullWidth
              value={projectElmnt?.customerLocation || ""}
              onChange={(e) => handleInputChange(e, projectId, "customerLocation")}
              SelectProps={{
                displayEmpty: true,
                renderValue: (v) => v || "Select Country",
                MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
              }}
            >
              {countryOptions.filter((c) => c.value).map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Role in the project"
              fullWidth
              value={projRoles[0] || ""}
              onChange={(e) =>
                handleProjectUpdate(projectId, {
                  roleInProject: e.target.value ? [e.target.value] : [],
                })
              }
              SelectProps={{
                displayEmpty: true,
                MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
              }}
            >
              <MenuItem value="">Select role</MenuItem>
              {roleInProject.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Search to add skills used in this project
            </Typography>
            <Autocomplete
              multiple
              options={techSkillOptions}
              getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label ?? "")}
              isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
              value={techSkillOptions.filter((o) => projSkills.includes(o.value))}
              onChange={(_, selected) => {
                const vals = (selected || []).map((o) => o.value);
                handleProjectUpdate(projectId, { skill: vals });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search to add skills used in this project"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                        {params.InputProps?.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    size="small"
                    onDelete={getTagProps({ index }).onDelete}
                    {...getTagProps({ index })}
                  />
                ))
              }
            />
            {projSkills.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Skills added.
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              placeholder="Enter description here"
              fullWidth
              multiline
              rows={4}
              value={projectElmnt?.description || ""}
              onChange={(e) => handleInputChange(e, projectId, "description")}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>
    );
  };

  const submitEditResults = async () => {
    await patchCv({
      cvId,
      projects: [{ _id: projectId, ...projectElmnt }],
    }).unwrap();
    handleClose();
    toast.info("Projects updated successfully");
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const years = [];
  for (let year = 2023; year >= 1940; year--) {
    years.push(year);
  }

  return (
    <div id="Projects">
      <ToastContainer autoClose={1500} />
      <Grid container xs={12}>
        {projs.length === 0 ? (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ marginTop: "10px" }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        InputLabelProps={{ shrink: true }}
                        label="Project Name"
                        placeholder="Enter Name"
                        fullWidth
                        autoFocus
                        error={!!errors.name}
                        helperText={errors?.name ? errors.name?.message : ""}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="sector"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Sector"
                        placeholder="Enter Name"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        error={!!errors.sector}
                        helperText={errors?.sector ? errors.sector?.message : ""}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="startDate"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <DatePicker
                        label="Start Date"
                        inputFormat="dd-MM-yyyy"
                        value={monthYearToDate(watch("startMonth"), watch("startYear"))}
                        onChange={(date) => {
                          const { month, year } = dateToMonthYear(date);
                          setValue("startMonth", month);
                          setValue("startYear", year);
                          if (date) setLogStartYear(year);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={!!errors.startMonth || !!errors.startYear}
                            helperText={errors?.startMonth?.message || errors?.startYear?.message || ""}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="endDate"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <DatePicker
                        label="End Date"
                        inputFormat="dd-MM-yyyy"
                        disabled={watch("onGoing")}
                        value={watch("onGoing") ? null : monthYearToDate(watch("endMonth"), watch("endYear"))}
                        onChange={(date) => {
                          const { month, year } = dateToMonthYear(date);
                          setValue("endMonth", month);
                          setValue("endYear", year);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            placeholder="DD-MM-YYYY"
                            error={!!errors.endMonth || !!errors.endYear}
                            helperText={errors?.endMonth?.message || errors?.endYear?.message || ""}
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    )}
                  />
                </Grid>

              <Grid item xs={12}>
                <FormControl>
                  <FormHelperText error={!!errors.onGoing}>
                    {errors?.onGoing ? errors.onGoing?.message : ""}
                  </FormHelperText>
                  <Controller
                    name="onGoing"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <FormControlLabel
                        label="Ongoing"
                        sx={{ margin: "0px" }}
                        control={
                          <Checkbox
                            onChange={onChange}
                            checked={value}
                            {...field}
                            ref={null}
                          />
                        }
                      >
                        {!value &&
                          watch("onGoing", () => {
                            console.log("onGoing checkbox is unchecked");
                            setValue("endYear", "");
                            setValue("endMonth", "");
                          })}
                      </FormControlLabel>
                    )}
                  />
                </FormControl>
              </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="customerName"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Customer Name"
                        placeholder="Enter Name"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.customerName}
                        helperText={errors?.customerName ? errors.customerName?.message : ""}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="customerLocation"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Customer Location"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (v) => v || "Select Country",
                          MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
                        }}
                      >
                        {countryOptions.filter((c) => c.value).map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="roleInProject"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <TextField
                        select
                        label="Role in the project"
                        fullWidth
                        value={selectedRole[0] || ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          const arr = v ? [v] : [];
                          setSelectedRole(arr);
                          setValue("roleInProject", arr);
                        }}
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
                        }}
                        error={!!errors.roleInProject}
                        helperText={errors?.roleInProject ? errors.roleInProject?.message : ""}
                      >
                        <MenuItem value="">Select role</MenuItem>
                        {roleInProject.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Search to add skills used in this project
                  </Typography>
                  <Controller
                    name="skill"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange } }) => (
                      <Autocomplete
                        multiple
                        options={techSkillOptions}
                        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label ?? "")}
                        isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                        value={techSkillOptions.filter((o) => selectedSkill.includes(o.value))}
                        onChange={(_, selected) => {
                          const vals = (selected || []).map((o) => o.value);
                          setSelectedSkill(vals);
                          setValue("skill", vals);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search to add skills used in this project"
                            error={!!errors.skill}
                            helperText={errors?.skill ? errors.skill?.message : ""}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip
                              key={option.value}
                              label={option.label}
                              size="small"
                              onDelete={getTagProps({ index }).onDelete}
                              {...getTagProps({ index })}
                            />
                          ))
                        }
                      />
                    )}
                  />
                  {selectedSkill.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Skills added.
                    </Typography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        placeholder="Enter description here"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.description}
                        helperText={errors?.description ? errors.description?.message : ""}
                      />
                    )}
                  />
                </Grid>
              {/* <Grid item xs={12}>
                <Controller
                  name="projectUrl"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Url to Project"
                      fullWidth
                      error={!!errors.projectUrl}
                      helperText={
                        errors?.projectUrl ? errors.projectUrl?.message : ""
                      }
                    />
                  )}
                />
              </Grid> */}

              {/* <Grid item xs={12}>
                <FormControl>
                  <FormHelperText error={!!errors.keyAssignments}>
                    {errors?.keyAssignments
                      ? errors.keyAssignments?.message
                      : ""}
                  </FormHelperText>
                  <Controller
                    name="keyAssignments"
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Tooltip title="You can only add up to two key assignments">
                        <FormControlLabel
                          label="Key Assignment"
                          sx={{ margin: "0px" }}
                          control={
                            <Checkbox
                              onChange={onChange}
                              checked={value}
                              {...field}
                              ref={null}
                              disabled={isCheckboxDisabled}
                            />
                          }
                        />
                      </Tooltip>
                    )}
                  />
                </FormControl>
              </Grid> */}
              <Grid item xs={12} sx={{ marginTop: "1rem" }}>
                <Button
                  variant="contained"
                  type="submit"
                  sx={{
                    width: 82, // updated width
                    height: 42, // px
                    backgroundColor: "#463C3B",
                    borderRadius: "8px", // Corner/Small
                    padding: "8px 22px", // top/bottom 8px, left/right 22px
                    opacity: 1,
                    textTransform: "none", // keep text as-is
                    boxShadow: "none", // optional: remove default shadow
                  }}
                >
                  Save
                </Button>
              </Grid>
            </Grid>


            <Grid
              container
              sx={{ marginTop: "10px" }}
              justifyContent="flex-end"
            >
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between">
                  <Button
                    variant="outlined"
                    onClick={goToPrevStep}
                    sx={{
                      width: 84,
                      height: 42,
                      borderRadius: "8px",
                      borderWidth: "1px",
                      borderColor: "currentColor",
                      padding: "8px 22px",
                      opacity: 1,
                      textTransform: "none",
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={goToNextStep}
                    sx={{
                      width: 84, // px
                      height: 42, // px
                      borderRadius: "8px", // Corner/Small
                      padding: "8px 22px", // top/bottom 8px, left/right 22px
                      opacity: 1,
                      textTransform: "none", // keep text as-is
                      boxShadow: "none", // optional: remove default shadow
                    }}
                  >
                    Next
                  </Button>
                </Stack>
              </Grid>
            </Grid>
            </LocalizationProvider>
          </form>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ProjectDisplay boxes={projectsState} onBoxesChange={setProjectsState} handleRemove={handleRemove} handleEdit={handleEdit} />
            
              <Grid item xs={12} sx={{ marginTop: "2rem" }}>
                <Button
                  onClick={() => handleOpen("add")}
                  disableRipple
                  disableFocusRipple
                  sx={{
                    color: "black",
                    textTransform: "uppercase",
                    textDecoration: "underline",
                    textDecorationStyle: "solid",
                    fontFamily: "fontFamily",
                    fontWeight: "fontWeightMedium",
                    fontSize: "0.9375rem",
                    lineHeight: "26px",
                    letterSpacing: "0.46px",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px", // tight spacing between icon and text
                    padding: 0, // remove button padding
                    minWidth: "unset", // avoid default button minWidth
                  }}
                >
                  <AddIcon sx={{ fontSize: "1rem", color: "black" }} />
                  Add Another
                </Button>
              </Grid>

              <Grid
                container
                sx={{ marginTop: "30px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack direction="row" justifyContent="space-between">
                    <Button
                      variant="outlined"
                      onClick={goToPrevStep}
                      sx={{
                        width: 84, // width in px
                        height: 42, // height in px
                        borderRadius: "8px", // "Corner/Small" -> approx 8px
                        borderWidth: "1px",
                        borderColor: "currentColor", // uses current text/icon color
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keeps text as-is
                      }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={goToNextStep}
                      sx={{
                        width: 84, // px
                        height: 42, // px
                        borderRadius: "8px", // Corner/Small
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keep text as-is
                        boxShadow: "none", // optional: remove default shadow
                      }}
                    >
                      Next
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>


          </Grid>
        )}

      </Grid>

      {/* <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ marginTop: "20px", padding: "30px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ marginTop: "5px" }}
            >
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 0, pb: 0 }}
                    >
                      {label === "Key Assignments"
                        ? "Key Assignments"
                        : "Projects"}{" "}
                      &nbsp;
                      {label === "Projects" && (
                        <Tooltip
                          disableFocusListener
                          disableTouchListener
                          title="This section is mandatory"
                        >
                          <span style={{ color: "red" }}>*</span>
                        </Tooltip>
                      )}
                    </Typography>
                  </Grid>

                  {label !== "Key Assignments" && (
                    <>
                      <Grid item xs={1}>
                        <IconButton
                          aria-label="add"
                          onClick={() => handleOpen("add")}
                        >
                          <AddIcon color="primary" />
                        </IconButton>
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          aria-label="edit"
                          onClick={() => handleOpen("edit")}
                        >
                          <EditIcon color="primary" />
                        </IconButton>
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {projs?.map((proj) => (
                  <List key={proj._id} sx={{ padding: "0px" }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: cyan[700] }}>
                          <AssignmentIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ paddingLeft: "10px" }}
                        primary={
                          <Typography variant="subtitle2" components="div">
                            Project : {proj.name}
                          </Typography>
                        }
                        secondary={
                          <>
                            <Typography variant="caption" display="block">
                              Start Date: {proj.startMonth} {proj.startYear}
                            </Typography>

                            {proj.endMonth && proj.endYear && (
                              <Typography variant="caption" display="block">
                                End Date: {proj.endMonth} {proj.endYear}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Customer Name : {proj.customerName}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Sector : {proj.sector}
                            </Typography>

                            {proj.skill && (
                              <Typography variant="caption" display="block">
                                Skill(s): {proj.skill.join(", ")}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Role : {proj.roleInProject.join(", ")}
                            </Typography>

                            {proj.onGoing && (
                              <Typography variant="caption" display="block">
                                Done : {proj.onGoing ? "No" : "Yes"}
                              </Typography>
                            )}

                            {proj.keyAssignments && (
                              <Typography variant="caption" display="block">
                                Key Assignment :{" "}
                                {proj.keyAssignments ? "Yes" : "No"}
                              </Typography>
                            )}

                            {proj.description && (
                              <Typography variant="caption" display="block">
                                Description: {proj.description}
                              </Typography>
                            )}

                            <Typography variant="caption" display="block">
                              Exclude in cv: {proj.excludeInCv ? "Yes" : "No"}
                            </Typography>
                          </>
                        }
                      />

                      <Grid
                        item
                        xs={12}
                        sm={3}
                        sx={{ textAlign: "right", paddingRight: "40px" }}
                      >
                        <FormControl>
                          <FormHelperText error={!!errors.excludeInCv}>
                            {errors?.excludeInCv
                              ? errors.excludeInCv?.message
                              : ""}
                          </FormHelperText>
                          <Controller
                            key={proj._id}
                            name={`excludeInCv-${proj._id}`}
                            control={control}
                            render={({
                              field: { onChange, value, ...field },
                            }) => (
                              <Tooltip title="This will exclude the section from the CV">
                                <FormControlLabel
                                  sx={{ margin: "0px" }}
                                  control={
                                    <Switch
                                      onChange={(e) => {
                                        const newExcludeInCv =
                                          e.target.checked;

                                        // Update the local state for the specific certificate immediately
                                        setLocalExcludeInCv(
                                          (prevLocalExcludeInCv) => ({
                                            ...prevLocalExcludeInCv,
                                            [proj._id]: newExcludeInCv,
                                          })
                                        );

                                        handleToggleChange(
                                          newExcludeInCv,
                                          proj._id,
                                          "excludeInCv"
                                        );

                                        console.log(
                                          "Toggle updated successfully"
                                        );
                                      }}
                                      checked={
                                        localExcludeInCv[proj._id] || false
                                      }
                                      {...field}
                                      ref={null}
                                    />
                                  }
                                />
                              </Tooltip>
                            )}
                          />
                        </FormControl>
                      </Grid>
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid> */}

      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >

        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>

            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="center" position="relative">

                {/* Centered title */}
                <Typography variant="h6" component="div">
                  Add a New Project
                </Typography>

                {/* Cancel icon on the left */}
                <IconButton
                  onClick={handleClose}
                  sx={{ position: "absolute", right: 8 }} // positions it to the left
                >
                  <CancelIcon />
                </IconButton>

              </Box>
            </DialogTitle>
            <DialogContent>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid
                  container
                  spacing={2}
                  justifyContent="center"
                  sx={{ marginTop: "10px" }}
                >
                  <Grid item xs={12}>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Project Name"
                          placeholder="Enter Name"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          autoFocus
                          error={!!errors.name}
                          helperText={errors?.name ? errors.name?.message : ""}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="sector"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Sector"
                          placeholder="Enter Name"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          error={!!errors.sector}
                          helperText={errors?.sector ? errors.sector?.message : ""}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="startDate"
                      control={control}
                      render={() => (
                        <DatePicker
                          label="Start Date"
                          inputFormat="dd-MM-yyyy"
                          value={monthYearToDate(watch("startMonth"), watch("startYear"))}
                          onChange={(date) => {
                            const { month, year } = dateToMonthYear(date);
                            setValue("startMonth", month);
                            setValue("startYear", year);
                            if (date) setLogStartYear(year);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={!!errors.startMonth || !!errors.startYear}
                              helperText={errors?.startMonth?.message || errors?.startYear?.message || ""}
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="endDate"
                      control={control}
                      render={() => (
                        <DatePicker
                          label="End Date"
                          inputFormat="dd-MM-yyyy"
                          disabled={watch("onGoing")}
                          value={watch("onGoing") ? null : monthYearToDate(watch("endMonth"), watch("endYear"))}
                          onChange={(date) => {
                            const { month, year } = dateToMonthYear(date);
                            setValue("endMonth", month);
                            setValue("endYear", year);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              placeholder="DD-MM-YYYY"
                              error={!!errors.endMonth || !!errors.endYear}
                              helperText={errors?.endMonth?.message || errors?.endYear?.message || ""}
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl>
                      <FormHelperText error={!!errors.onGoing}>
                        {errors?.onGoing ? errors.onGoing?.message : ""}
                      </FormHelperText>
                      <Controller
                        name="onGoing"
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => (
                          <FormControlLabel
                            label="Ongoing"
                            sx={{ margin: "0px" }}
                            control={
                              <Checkbox
                                onChange={(e) => {
                                  onChange(e);
                                  if (e.target.checked) {
                                    setValue("endYear", "");
                                    setValue("endMonth", "");
                                  }
                                }}
                                checked={value}
                                {...field}
                                ref={null}
                              />
                            }
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="customerName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Customer Name"
                          placeholder="Enter Name"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          error={!!errors.customerName}
                          helperText={errors?.customerName ? errors.customerName?.message : ""}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="customerLocation"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Customer Location"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{
                            displayEmpty: true,
                            renderValue: (v) => v || "Select Country",
                            MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
                          }}
                        >
                          {countryOptions.filter((c) => c.value).map((c) => (
                            <MenuItem key={c.value} value={c.value}>
                              {c.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Controller
                      name="roleInProject"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <TextField
                          select
                          label="Role in the project"
                          fullWidth
                          value={selectedRole[0] || ""}
                          onChange={(e) => {
                            const v = e.target.value;
                            const arr = v ? [v] : [];
                            setSelectedRole(arr);
                            setValue("roleInProject", arr);
                          }}
                          InputLabelProps={{ shrink: true }}
                          SelectProps={{
                            displayEmpty: true,
                            MenuProps: { anchorOrigin: { vertical: "bottom", horizontal: "center" } },
                          }}
                          error={!!errors.roleInProject}
                          helperText={errors?.roleInProject ? errors.roleInProject?.message : ""}
                        >
                          <MenuItem value="">Select role</MenuItem>
                          {roleInProject.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Search to add skills used in this project
                    </Typography>
                    <Controller
                      name="skill"
                      control={control}
                      rules={{ required: true }}
                      render={({ field: { onChange } }) => (
                        <Autocomplete
                          multiple
                          options={techSkillOptions}
                          getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt?.label ?? "")}
                          isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                          value={techSkillOptions.filter((o) => selectedSkill.includes(o.value))}
                          onChange={(_, selected) => {
                            const vals = (selected || []).map((o) => o.value);
                            setSelectedSkill(vals);
                            setValue("skill", vals);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Search to add skills used in this project"
                              error={!!errors.skill}
                              helperText={errors?.skill ? errors.skill?.message : ""}
                            />
                          )}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip
                                key={option.value}
                                label={option.label}
                                size="small"
                                onDelete={getTagProps({ index }).onDelete}
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                        />
                      )}
                    />
                  {selectedSkill.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                      Skills added.
                    </Typography>
                  )}
                  </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        placeholder="Enter description here"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.description}
                        helperText={errors?.description ? errors.description?.message : ""}
                      />
                    )}
                  />
                </Grid>
                {/* <Grid item xs={12}>
                  <Controller
                    name="projectUrl"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Url to Project"
                        fullWidth
                        error={!!errors.projectUrl}
                        helperText={
                          errors?.projectUrl ? errors.projectUrl?.message : ""
                        }
                      />
                    )}
                  />
                </Grid> */}

                {/* <Grid item xs={12}>
                  <FormControl>
                    <FormHelperText error={!!errors.keyAssignments}>
                      {errors?.keyAssignments
                        ? errors.keyAssignments?.message
                        : ""}
                    </FormHelperText>
                    <Controller
                      name="keyAssignments"
                      control={control}
                      render={({ field: { onChange, value, ...field } }) => (
                        <Tooltip title="You can only add up to two key assignments">
                          <FormControlLabel
                            label="Key Assignment"
                            sx={{ margin: "0px" }}
                            control={
                              <Checkbox
                                onChange={onChange}
                                checked={value}
                                {...field}
                                ref={null}
                                disabled={isCheckboxDisabled}
                              />
                            }
                          />
                        </Tooltip>
                      )}
                    />
                  </FormControl>
                </Grid> */}
              </Grid>
              </LocalizationProvider>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ marginTop: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" justifyContent="space-between">
                    <Button
                      variant="outlined" // outlined style
                      sx={{
                        width: 84, // width in px
                        height: 42, // height in px
                        borderRadius: "8px", // "Corner/Small" -> approx 8px
                        borderWidth: "1px",
                        borderColor: "currentColor", // uses current text/icon color
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keeps text as-is
                      }}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{
                        width: 84, // px
                        height: 42, // px
                        borderRadius: "8px", // Corner/Small
                        padding: "8px 22px", // top/bottom 8px, left/right 22px
                        opacity: 1,
                        textTransform: "none", // keep text as-is
                        boxShadow: "none", // optional: remove default shadow
                      }}
                      variant="contained"
                      type="submit"
                    >
                      Add
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Certifications</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ marginTop: "5px" }}
              >
                <Grid item xs={12}>
                  {projs?.map((proj) => (
                    <List key={proj._id} sx={{ padding: "0px" }}>
                      <ListItem
                        sx={{ paddingLeft: "10px" }}
                        secondaryAction={
                          <Stack
                            alignItems="flex-end"
                            direction="row"
                            paddingBottom="15px"
                          >
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(proj._id, "editnew")}
                              sx={{ margin: "0px", padding: "0px" }}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(proj._id)}
                              sx={{ margin: "0px", padding: "0px" }}
                            >
                              <DeleteIcon sx={{ color: red[400] }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          sx={{ paddingRight: "20px", margin: "0px" }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Project: {proj.name}
                            </Typography>
                          }
                          secondary={
                            <>
                              <Typography variant="caption" display="block">
                                Start Date: {proj.startMonth} {proj.startYear}
                              </Typography>
                              <Typography variant="caption" display="block">
                                End Date: {proj.endMonth} {proj.endYear}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Customer Name: {proj.customerName}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Sector: {proj.sector}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Skill(s): {proj.skill.join(", ")}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Role: {proj.roleInProject.join(", ")}{" "}
                              </Typography>
                              Done: {proj.onGoing ? "No" : "Yes"}
                              {/* <Typography variant="caption" display="block">
                                Key Assignment:{" "}
                                {proj.keyAssignments ? "Yes" : "No"}
                              </Typography> */}
                              <Typography variant="caption" display="block">
                                Description: {proj.description}
                              </Typography>
                              <Typography variant="caption" display="block">
                                Exclude in cv: {proj.excludeInCv ? "Yes" : "No"}
                              </Typography>
                            </>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={(event) => {
              event.preventDefault();
              submitEditResults();
            }}
          >
            <DialogTitle>
              <Box display="flex" alignItems="center" justifyContent="center" position="relative">

                {/* Centered title */}
                <Typography variant="h6" component="div">
                  Edit Project
                </Typography>

                {/* Cancel icon on the left */}
                <IconButton
                  onClick={handleClose}
                  sx={{ position: "absolute", right: 8 }} // positions it to the left
                >
                  <CancelIcon />
                </IconButton>

              </Box>
            </DialogTitle>
            <DialogContent>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" justifyContent="space-between">
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default Projects;